import { useState, useEffect, useCallback } from "react";
import { DsaPartner, PartnerDocument } from "@/types";
import { useAuth } from "@/contexts/AuthContext";
import {
  subscribeToDsaPartners,
  addDsaPartner,
  updateDsaPartner,
  deleteDsaPartner,
  uploadDsaPartnerDocument,
  deleteDsaPartnerDocument,
  toggleDsaPartnerFavorite,
} from "@/services/dsaPartnerService";

export function useDsaPartners({ enabled = true }: { enabled?: boolean } = {}) {
  const { user } = useAuth();
  const [partners, setPartners] = useState<DsaPartner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled || !user?.id) {
      setPartners([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const unsubscribe = subscribeToDsaPartners(user.id, (updatedPartners) => {
      setPartners(updatedPartners);
      setLoading(false);
    });

    return () => {
      unsubscribe();
    };
  }, [user?.id, enabled]);

  const addPartner = useCallback(
    async (partner: Omit<DsaPartner, "id">): Promise<DsaPartner> => {
      if (!user?.id) {
        throw new Error("User must be authenticated to add a DSA partner");
      }

      try {
        const newPartner = await addDsaPartner(partner, user.id);
        // Optimistically add the new partner to the list so it appears immediately
        setPartners((prev) => {
          const exists = prev.some((p) => p.id === newPartner.id);
          if (exists) return prev;
          const merged = [newPartner, ...prev];
          merged.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
          return merged;
        });
        return newPartner;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to add DSA partner";
        setError(errorMessage);
        throw err;
      }
    },
    [user?.id],
  );

  const updatePartner = useCallback(
    async (partnerId: string, updates: Partial<DsaPartner>): Promise<void> => {
      if (!user?.id) {
        throw new Error("User must be authenticated to update a DSA partner");
      }

      try {
        if (updates.lastVisitDate && !updates.lastVisitedBy) {
          updates.lastVisitedBy = user.userId;
        }
        await updateDsaPartner(partnerId, updates);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to update DSA partner";
        setError(errorMessage);
        throw err;
      }
    },
    [user?.id],
  );

  const deletePartner = useCallback(
    async (partnerId: string): Promise<void> => {
      if (!user?.id) {
        throw new Error("User must be authenticated to delete a DSA partner");
      }

      try {
        const partner = partners.find((p) => p.id === partnerId);
        if (!partner) {
          throw new Error("DSA partner not found");
        }

        await deleteDsaPartner(partnerId, partner);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to delete DSA partner";
        setError(errorMessage);
        throw err;
      }
    },
    [user?.id, partners],
  );

  const uploadDocument = useCallback(
    async (file: File, partnerId?: string): Promise<PartnerDocument> => {
      if (!user?.id) {
        throw new Error("User must be authenticated to upload a document");
      }

      try {
        return await uploadDsaPartnerDocument(file, user.id, partnerId);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to upload document";
        setError(errorMessage);
        throw err;
      }
    },
    [user?.id],
  );

  const deleteDocument = useCallback(
    async (fileUrl: string): Promise<void> => {
      if (!user?.id) {
        throw new Error("User must be authenticated to delete a document");
      }

      try {
        await deleteDsaPartnerDocument(fileUrl);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to delete document";
        setError(errorMessage);
        throw err;
      }
    },
    [user?.id],
  );

  const toggleFavorite = useCallback(
    async (partnerId: string, isFavorite: boolean): Promise<void> => {
      if (!user?.id) {
        throw new Error("User must be authenticated to toggle favorite");
      }

      try {
        await toggleDsaPartnerFavorite(partnerId, isFavorite);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to toggle favorite";
        setError(errorMessage);
        throw err;
      }
    },
    [user?.id],
  );

  const getPartner = useCallback(
    (partnerId: string): DsaPartner | undefined => {
      return partners.find((p) => p.id === partnerId);
    },
    [partners],
  );

  return {
    partners,
    loading,
    error,
    addPartner,
    updatePartner,
    deletePartner,
    uploadDocument,
    deleteDocument,
    toggleFavorite,
    getPartner,
  };
}
